import { FC } from 'react'

import { Link, useParams } from '@remix-run/react'

import { IconArrowRight } from '~/components'
import { NO_IMAGE_URL } from '~/constants'
import { twMerge } from '~/lib/utils'
import { Availability, Space } from '~/models/types'
import { encodeImageUrl } from '~/utils/encode-image-url'

type SpaceCardProps = {
  space: Pick<
    Space,
    | 'id'
    | 'name'
    | 'availability'
    | 'maxPeople'
    | 'images'
    | 'type'
    | 'monthlyCostExcludingTax'
    | 'monthlyCostIncludingTax'
    | 'isMonthlyCostPrivate'
  >
  className?: string
}

const availabilityToJaText = (availability: Availability) => {
  switch (availability) {
    case 'AVAILABLE':
      return '空きあり'
    case 'AVAILABLE_SOON':
      return '空き予定'
    case 'UNAVAILABLE':
      return '空きなし'
    default:
      return availability
  }
}

export const SpaceCard: FC<SpaceCardProps> = ({ space, className }) => {
  const { availability } = space
  const isAvailableOrSoon =
    availability === 'AVAILABLE' || availability === 'AVAILABLE_SOON'
  const { prefectureSlug, officeId } = useParams<
    'prefectureSlug' | 'officeId'
  >()
  const sortedImages = space.images.sort((a, b) => a.priority - b.priority)
  const firstImage =
    sortedImages.find((image) => image.isEyeCatch) || sortedImages[0]

  return (
    <Link
      className={twMerge(
        'block overflow-hidden rounded border duration-100 hover:opacity-80',
        isAvailableOrSoon ? 'border-brand-light' : 'border-grayBorder',
        className,
      )}
      to={`/prefectures/${prefectureSlug}/offices/${officeId}/plans/${space.id}`}
    >
      <div
        className={twMerge(
          'flex justify-between p-2 font-bold leading-[18px] text-white',
          isAvailableOrSoon ? 'bg-brand' : 'bg-grayText',
        )}
      >
        <div className="flex gap-x-2">
          {space.type.map((t) => (
            <span className="border border-white px-2 text-[12px]" key={t.id}>
              {t.name}
            </span>
          ))}
        </div>
        <span className="text-[13px]">
          {availabilityToJaText(availability)}
        </span>
      </div>
      <div
        className={twMerge(
          'flex flex-col gap-4 p-4 md:flex-row',
          isAvailableOrSoon ? 'bg-brand-bg' : 'bg-grayBg',
        )}
      >
        <div className="flex items-center gap-x-2 md:w-[330px]">
          <div className="h-20 shrink-0 basis-[120px] self-stretch bg-grayBorder">
            <img
              alt={space.name}
              className="h-full w-full object-cover"
              src={firstImage?.url ? encodeImageUrl(firstImage.url) : NO_IMAGE_URL}
            />
          </div>
          <div className="line-clamp-3 text-body-bold">{space.name}</div>
        </div>
        <div className="flex min-h-[52px] w-full items-stretch gap-x-2 md:w-[290px]">
          <div className="flex flex-1 flex-col justify-center rounded bg-white text-center">
            {space.monthlyCostExcludingTax === null ||
            space.monthlyCostIncludingTax === null ||
            space.isMonthlyCostPrivate ? (
              <div
                className={twMerge(
                  'leading-[140%]',
                  isAvailableOrSoon && 'text-cta',
                )}
              >
                <span className="text-[18px] font-bold">お問い合わせ</span>
              </div>
            ) : (
              <>
                <div
                  className={twMerge(
                    'leading-[140%]',
                    isAvailableOrSoon && 'text-cta',
                  )}
                >
                  <span className="text-[18px] font-bold">
                    {space.monthlyCostExcludingTax.toLocaleString()}
                  </span>
                  <span className="text-[12px]">円/月</span>
                </div>
                <div className="text-[10px] leading-[16px] text-grayText">
                  (税込{space.monthlyCostIncludingTax.toLocaleString()}
                  円/月)
                </div>
              </>
            )}
          </div>
          <div className="flex flex-1 items-center justify-center rounded bg-white">
            {space.maxPeople !== null ? (
              <div>
                <span className="text-body-bold">{space.maxPeople}</span>
                <span className="text-[12px] leading-[140%]">名</span>
              </div>
            ) : (
              <span className="text-body-bold">-</span>
            )}
          </div>
        </div>
        <div className="relative flex shrink-0 items-center justify-center md:w-[150px]">
          <div
            className={twMerge(
              'flex items-center gap-x-1',
              'md:block md:w-full md:rounded md:border md:bg-white md:px-6 md:py-2 md:text-center',
              isAvailableOrSoon ? 'md:border-brand' : 'md:border-cta',
            )}
          >
            <span
              className={twMerge(
                'text-[14px] font-bold leading-[18px]',
                isAvailableOrSoon ? 'text-brand' : 'text-cta',
              )}
            >
              {isAvailableOrSoon ? '詳細を見る' : '空き待ち予約はこちら'}
            </span>
            <div className="md:hidden">
              <IconArrowRight color={isAvailableOrSoon ? 'brand' : 'cta'} />
            </div>
          </div>
          <div className="absolute right-2 hidden md:block">
            <IconArrowRight color={isAvailableOrSoon ? 'brand' : 'cta'} />
          </div>
        </div>
      </div>
    </Link>
  )
}
